"use client";

import { useEffect } from "react";
import Image from "next/image";
import { Minus, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useDesktopBridge } from "@/hooks/useDesktopShellWindow";
import { DRAG_REGION_CLASS, NO_DRAG_REGION_CLASS } from "@/lib/guide/desktop-shell";
import { cn } from "@/lib/utils";
import logo from "@/public/logo-orange.png";

// The desktop app's window at sign-in, setup and the debrief: a white panel
// on the transparent window, with the strip the rep drags it by, since the
// window has no frame of its own. During a call the teleprompter draws the
// whole window instead (GuideApp). Links that leave Guide open in the rep's
// browser: this window is Guide and nothing else.
//
// The strip carries the platform's own way to close and minimize: on macOS
// the system's red and yellow buttons, which the app places at the strip's
// left and this panel shows only while it is up (the teleprompter has none),
// and elsewhere Windows' minimize and close, drawn at the right. Hiding the
// window is the keyboard shortcut's job, named on the setup screen.

// Pages that stay in the window: Guide itself and the sign-in. Any other
// page on the site (the debrief's results link) belongs in the browser,
// where the rest of Salesably is. Links to other sites and links that ask
// for a new window are the main process's to route (desktop/src/window.js).
const WINDOW_PATHS = new Set(["/guide", "/"]);

function useLinksOpenInBrowser(bridge) {
  useEffect(() => {
    if (!bridge) return undefined;
    const onClick = (event) => {
      const anchor = event.target.closest?.("a[href]");
      if (!anchor || anchor.target === "_blank" || event.defaultPrevented) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin || WINDOW_PATHS.has(url.pathname)) return;
      event.preventDefault();
      bridge.openExternal(url.href);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [bridge]);
}

/** macOS's own buttons, up while the panel is: the teleprompter has none. */
function useSystemWindowControls(bridge) {
  useEffect(() => {
    if (!bridge?.setWindowControls) return undefined;
    bridge.setWindowControls(true);
    return () => {
      bridge.setWindowControls(false);
    };
  }, [bridge]);
}

/** Windows' minimize and close, which a frameless window there does not draw. */
function PageWindowControls({ bridge }) {
  return (
    <div className={cn("ml-auto flex items-center", NO_DRAG_REGION_CLASS)}>
      <Button variant="ghost" size="xs" className="w-8 px-0" aria-label="Minimize" onClick={() => bridge.minimize()}>
        <Minus className="h-4 w-4" aria-hidden="true" />
      </Button>
      <Button variant="ghost" size="xs" className="w-8 px-0" aria-label="Close" onClick={() => bridge.close()}>
        <X className="h-4 w-4" aria-hidden="true" />
      </Button>
    </div>
  );
}

/**
 * @param {object} props
 * @param {React.ReactNode} props.children the page, scrolling inside the panel.
 * @param {string} [props.className] layout for the scrolling area.
 */
export default function DesktopPanel({ children, className }) {
  const bridge = useDesktopBridge();
  const systemControls = bridge?.platform === "darwin";
  useLinksOpenInBrowser(bridge);
  useSystemWindowControls(bridge);

  return (
    <div className="flex h-screen flex-col overflow-hidden rounded-2xl border border-line bg-white">
      <div
        className={cn(
          "flex h-10 shrink-0 select-none items-center gap-2 border-b border-line bg-surface-chrome-light px-3",
          // Room for the red, yellow and green buttons the system draws here.
          systemControls && "pl-20",
          DRAG_REGION_CLASS,
        )}
      >
        <Image src={logo} alt="" width={18} height={18} />
        <span className="text-xs font-semibold text-gray-700">Salesably Guide</span>
        {!systemControls && bridge?.close && <PageWindowControls bridge={bridge} />}
      </div>
      <div className={cn("min-h-0 flex-1 overflow-y-auto px-6 py-6", className)}>{children}</div>
    </div>
  );
}
